import React, { FC, useEffect, useState } from "react";

import Chip from "@mui/material/Chip";
import FormHelperText from "@mui/material/FormHelperText";
import CheckIcon from "@mui/icons-material/Check";

import { FormChipsInput, ActionableItem, SelectableItem } from "stentor-models";

import { Styleable } from "../../types";

import { FormFieldProps } from "../FormDisplay";

import styles from "./FormFieldChips.module.scss";

export function isSelectableItem(item: ActionableItem | SelectableItem): item is SelectableItem {
    return !!item && !(item as ActionableItem).url;
}

export function isActionableItem(item: ActionableItem | SelectableItem): item is ActionableItem {
    return !!(item as ActionableItem).url;
}

export interface FormFieldChipsProps extends FormFieldProps {
    readonly field: Styleable<FormChipsInput>;
    /** Optional prefilled value that takes precedence over items' selected state.
     *  Only used for initial mount; late-arriving updates (after mount) are not supported. */
    readonly value?: string[];
}

export const FormFieldChips: FC<FormFieldChipsProps> = (props) => {
    const { field, onFieldChange, errorMessage, value } = props;

    /**
     * Selected chips
     */
    // Use prefilled value if provided, otherwise look for pre-selected items
    const selectedFromProps = value || props.field.items
        .filter(isSelectableItem) // filter out actionable items
        .filter((item) => item.selected)
        .map((item) => item.id);
    const [selectedChips, setSelectedChips] = useState<string[]>(selectedFromProps); // array!

    const handleChipClick = (id: string) => {
        const checked = selectedChips.includes(id);

        if (field.radio) {
            setSelectedChips((_prevOptions) => {
                if (!checked) {
                    return [id]; // add
                } else {
                    return []; // delete
                }
            });
        } else {
            setSelectedChips((prevOptions) => {
                if (!checked) {
                    return [...prevOptions, id]; // add
                } else {
                    return prevOptions.filter((option) => option !== id); // delete
                }
            });
        }
    };

    useEffect(() => {
        if (typeof onFieldChange === "function") {
            onFieldChange(field.name, selectedChips);
        }
    }, [selectedChips, field.name, onFieldChange]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                alignItems: "center",
            }}>
            <div
                className={`${styles.xapp_chips_title_container}${!field.title ? "__empty" : ""}`}
                style={{ ...field.style }}>
                <p>{field.title}</p>
            </div>

            <div className={styles.xapp_chips_container}>
                {field.items.map((item, index) => {
                    if (isSelectableItem(item)) {
                        return (
                            <div key={index} className={styles.xapp_chip_wrapper}>
                                <Chip
                                    label={item.label}
                                    clickable
                                    color={selectedChips.includes(item.id) ? "primary" : "default"}
                                    onClick={() => handleChipClick(item.id)}
                                    icon={
                                        selectedChips.includes(item.id) ? <CheckIcon /> : undefined
                                    }
                                    className={`${styles.xapp_checkbox_chip} ${
                                        selectedChips.includes(item.id)
                                            ? styles.xapp_chip_clicked
                                            : ""
                                    }`}
                                />
                            </div>
                        );
                    } else if (isActionableItem(item)) {
                        return (
                            <div key={index} className={styles.xapp_chip_wrapper}>
                                <Chip
                                    sx={{
                                        "& .MuiChip-label": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                    label={item.label}
                                    component={"a"}
                                    clickable
                                    color={selectedChips.includes(item.id) ? "primary" : "default"}
                                    icon={
                                        selectedChips.includes(item.id) ? <CheckIcon /> : undefined
                                    }
                                    className={`${styles.xapp_checkbox_chip} ${
                                        selectedChips.includes(item.id)
                                            ? styles.xapp_chip_clicked
                                            : ""
                                    }`}
                                    href={item.url}
                                />
                            </div>
                        );
                    } else {
                        return <></>;
                    }
                })}
            </div>

            {errorMessage && (
                <FormHelperText error style={{ marginLeft: "14px" }}>
                    {errorMessage}
                </FormHelperText>
            )}
        </div>
    );
};

export default FormFieldChips;
